import type { CSSProperties } from 'react';
import { ExternalLink, Gift } from 'lucide-react';
import { AnimatedIcon } from '@/components/AnimatedIcon';
import type { LinkItem, LinkTag } from '@/data/links';

interface GlowCardProps extends LinkItem {
  index: number;
  total: number;
  visible: boolean;
  open: boolean;
  onOpen: (open: boolean) => void;
  onLinkClick?: (id: string) => void;
}

const TAGS: Partial<Record<LinkTag, string>> = { free: 'חינם', freemium: 'חינם בסיסי', premium: 'פרימיום', deal: 'דיל שותפים', recommended: 'מומלץ', popular: 'פופולרי', beta: 'בטא', new: 'חדש', 'open-source': 'קוד פתוח', israeli: 'ישראלי', 'news-channel': 'ערוץ חדשות', 'quiet-news': 'שקט' };

const coarse = () => window.matchMedia('(pointer: coarse)').matches;
const scrolling = () => document.querySelector<HTMLElement>('[data-rd-zap-effects]')?.dataset.scrolling === 'true';

/**
 * The original tilted glass stack, with clipped local lighting and no live backdrop blur.
 * The card body opens the description. Only the arrow square follows the URL.
 */
export const GlowCard = ({ title, subtitle, description, url, icon, color, animation, index, total, visible, open, onOpen, id, tag, affiliateBenefit, onLinkClick }: GlowCardProps) => {
  if (!icon || !url) return null;
  const rotation = index % 2 === 0 ? -1.2 : 1.2;
  const xNudge = index % 2 === 0 ? -4 : 4;

  return (
    <article
      data-rd-card
      data-rd-color={color}
      onPointerEnter={() => { if (!coarse() && !scrolling()) onOpen(true); }}
      onPointerLeave={() => { if (!coarse()) onOpen(false); }}
      onFocus={() => { if (!coarse()) onOpen(true); }}
      onBlur={(e) => {
        if (coarse()) return;
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        onOpen(false);
      }}
      className={`relative block rounded-2xl transition-opacity duration-200 ease-out ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      style={{
        zIndex: open ? 50 : total - index,
        transform: `translateX(${xNudge}px) rotate(${rotation}deg)`,
        marginBottom: '-10px',
        '--rd-color': color,
        '--rd-direction': index % 2 === 0 ? -1 : 1
      } as CSSProperties}
    >
      <div
        aria-hidden="true"
        className={`absolute inset-0 rounded-2xl transition-opacity duration-300 pointer-events-none ${open ? 'opacity-100' : 'opacity-0'}`}
        style={{ boxShadow: `0 20px 50px ${color}30, 0 8px 24px rgba(0,0,0,0.45)` }}
      />
      <div
        data-rd-surface
        className="rd-card-surface relative rounded-2xl overflow-hidden transition-[background,border-color,transform] duration-300"
        style={{
          transform: open ? 'translateY(-6px) scale(1.02)' : 'none',
          background: open
            ? `linear-gradient(145deg, ${color}22 0%, rgba(8,12,30,0.34) 60%)`
            : `linear-gradient(145deg, ${color}0a 0%, rgba(8,12,30,0.12) 60%)`,
          border: `1px solid ${open ? `${color}55` : 'rgba(214,226,255,0.11)'}`,
          textShadow: '0 1px 10px rgba(6,11,30,.9)'
        }}
      >
        <div data-rd-charge-overlay aria-hidden="true" className="rd-card-charge" />
        <div
          data-rd-bar
          className="absolute top-2 bottom-2 right-0 w-[2px] rounded-full transition-colors duration-400"
          style={{ background: open ? color : `${color}40`, boxShadow: open ? `0 0 8px ${color}60` : 'none' }}
        />
        <div
          className={`absolute top-0 left-0 right-0 h-px transition-opacity duration-400 ${open ? 'opacity-100' : 'opacity-40'}`}
          style={{ background: `linear-gradient(90deg, transparent, ${color}70, transparent)` }}
        />

        <button
          type="button"
          aria-expanded={open}
          aria-controls={`rd-description-${id}`}
          onClick={() => onOpen(!open)}
          className="relative block w-full cursor-pointer appearance-none border-0 bg-transparent p-4 pr-5 pl-14 text-right font-[inherit] text-inherit focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <div className="flex items-center gap-4">
            <div
              data-rd-icon
              className={`flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-transform duration-400 ${open ? 'scale-110' : ''}`}
              style={{
                background: `${color}${open ? '22' : '12'}`,
                border: `1px solid ${open ? `${color}45` : `${color}22`}`,
                boxShadow: open ? `0 0 20px ${color}25` : 'none'
              }}
            >
              <AnimatedIcon icon={icon} animation={animation} color={color} isHovered={open} />
            </div>

            <div className="flex-1 min-w-0">
              <span
                className="font-semibold text-sm block truncate transition-colors duration-300"
                style={{ color: open ? color : 'rgba(255,255,255,0.92)' }}
              >
                {title}
              </span>
              <p
                className="text-xs mt-0.5 leading-relaxed line-clamp-1 transition-colors duration-300"
                style={{ color: open ? '#d6e2ff' : '#b7c6d9' }}
              >
                {subtitle}
              </p>
              {(tag || affiliateBenefit) && <div className="mt-1 flex items-center gap-2 text-[10px] text-white/70">
                {tag && TAGS[tag] && <span className="rounded-full border border-white/15 px-1.5 py-0.5">{TAGS[tag]}</span>}
                {affiliateBenefit && <span className="flex items-center gap-1"><Gift className="h-3 w-3" aria-hidden="true" />הטבת הרשמה</span>}
              </div>}
              <div id={`rd-description-${id}`} aria-hidden={!open} className={`transition-[max-height,opacity] duration-500 ease-out ${open ? 'max-h-64 overflow-y-auto opacity-100 mt-1.5' : 'max-h-0 overflow-hidden opacity-0'}`}>
                <div className="h-px w-10 mb-1.5" style={{ background: `${color}35` }} />
                <p className="text-white/70 text-[11px] leading-relaxed">{description}</p>
                {affiliateBenefit && <p className="mt-1 flex items-center gap-1 text-[11px] leading-relaxed text-amber-200/90"><Gift className="h-3 w-3 shrink-0" aria-hidden="true" />{affiliateBenefit}</p>}
              </div>
            </div>
          </div>
        </button>

        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`פתיחת ${title} בחלון חדש`}
          aria-hidden={!open}
          tabIndex={open ? 0 : -1}
          onClick={(e) => {
            e.stopPropagation();
            if (!open) e.preventDefault();
            else onLinkClick?.(id);
          }}
          className={`absolute left-2 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border transition-[opacity,transform] duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
            open ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
          }`}
          style={{
            borderColor: `${color}55`,
            background: `${color}18`,
            color
          }}
        >
          <ExternalLink className="h-4 w-4" aria-hidden="true" />
        </a>
      </div>
    </article>
  );
};
